import {
    Component,
    computed,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    ViewEncapsulation,
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { CerrixSearchInputComponent } from "@app/shared/atoms/cerrix-search-input/cerrix-search-input.component";
import { CerrixLinkItemRowComponent } from "@app/shared/molecules/cerrix-link-item-row/cerrix-link-item-row.component";
import { CerrixTabDirective } from "@app/shared/molecules/cerrix-tabs/cerrix-tab.directive";
import { CerrixTabsComponent } from "@app/shared/molecules/cerrix-tabs/cerrix-tabs.component";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { CerrixConfirmDialogComponent } from "../cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { CerrixDialogService } from "../cerrix-dialog/cerrix-dialog-service.component";
import { scope } from "./i18n/scope";
import { LinkableItem } from "./models/linkable-item";

interface LinkItemRow {
    item: LinkableItem;
    label: string;
}

/**
 * Picks the items to link to the work item the caller is editing. The caller opens it through
 * CerrixDialogService.openDialog(name) and feeds it the items; the dialog closes itself on
 * every exit and reports what happened through confirmed or cancelled.
 */
@Component({
    selector: "app-cerrix-link-items-dialog",
    imports: [
        NgTemplateOutlet,
        CerrixConfirmDialogComponent,
        CerrixTabsComponent,
        CerrixTabDirective,
        CerrixSearchInputComponent,
        CerrixLinkItemRowComponent,
        TranslatePipe,
    ],
    templateUrl: "./cerrix-link-items-dialog.component.html",
    styleUrl: "./cerrix-link-items-dialog.component.scss",
    viewProviders: [provideTranslationScope(scope)],
    encapsulation: ViewEncapsulation.None,
    // Default change detection on purpose: cerrix-dialog opens by setting a field from the
    // caller, with no signal and no event in this view, so an OnPush view would stay unchecked
    // and the dialog would never appear.
})
export class CerrixLinkItemsDialogComponent {
    /** The whole heading, so the caller names the module it links to ("Link risk"). */
    readonly title = input.required<string>();

    /** The key CerrixDialogService opens this dialog by. */
    readonly name = input("link-items-dialog");

    readonly items = input.required<readonly LinkableItem[]>();

    /** The selection the dialog starts with, normally the ids that are already linked. */
    readonly selectedIds = input<readonly string[]>([]);

    readonly confirmed = output<LinkableItem[]>();
    readonly cancelled = output<void>();
    /** The caller decides what opening an item's details means. */
    readonly itemDetailsRequested = output<LinkableItem>();

    protected readonly dataUiid = "cerrix-link-items-dialog";

    protected readonly searchTerm = signal("");

    // Reseeds from the input, so reopening the dialog for another work item starts over
    // instead of keeping the previous draft.
    private readonly draft = linkedSignal(() => [...this.selectedIds()]);

    private readonly draftSet = computed(() => new Set(this.draft()));

    protected readonly allRows = computed(() => this.rowsOf(this.items()));

    protected readonly linkedRows = computed(() => {
        const draftSet = this.draftSet();

        return this.rowsOf(this.items().filter((item) => draftSet.has(item.id)));
    });

    private readonly dialogService = inject(CerrixDialogService);

    // Closing after a confirm must not also report a dismissal, and the dialog reports every
    // close the same way, so the confirm marks itself here.
    private confirming = false;

    protected isSelected(item: LinkableItem): boolean {
        return this.draftSet().has(item.id);
    }

    protected setSelected(item: LinkableItem, selected: boolean): void {
        this.draft.update((ids) =>
            selected ? [...ids, item.id] : ids.filter((id) => id !== item.id),
        );
    }

    protected confirm(): void {
        const selected = this.draftSet();

        this.confirming = true;
        this.confirmed.emit(this.items().filter((item) => selected.has(item.id)));
        this.dialogService.closeDialog(this.name());
        // closeDialog() does nothing when the dialog is already closed, which leaves the flag
        // set and swallows the next dismissal, so it is cleared here as well.
        this.confirming = false;
    }

    protected cancel(): void {
        this.dialogService.closeDialog(this.name());
    }

    protected onClosed(): void {
        if (this.confirming) {
            return;
        }

        // A dismissal drops the picks, so reopening with the same selectedIds starts from them
        // again instead of showing the cancelled picks.
        this.draft.set([...this.selectedIds()]);
        this.cancelled.emit();
    }

    private rowsOf(items: readonly LinkableItem[]): LinkItemRow[] {
        const term = this.searchTerm().trim().toLowerCase();

        return items
            .map((item) => ({ item, label: `${item.identifier} | ${item.name}` }))
            .filter((row) => term === "" || row.label.toLowerCase().includes(term));
    }
}
